// The fabricated "Or open a sample" breakdown — the full extraction-review
// screen for a picked sample. Nothing here talks to the network.
import React, { useCallback, useMemo, useRef, useState } from 'react';
import AnnotationLayer from './AnnotationLayer';
import { blastText } from './IngestReview';

const humanize = (s: string | undefined) => String(s || '').replace(/_/g, ' ');
const pct = (c: number | null | undefined) => (c == null ? null : `${Math.round(Number(c) * 100)}%`);

const displayValue = (v: unknown) => (v == null || String(v).trim() === '' ? '—' : v);

const LOW_CONFIDENCE = 0.9;

const RING_R = 7.5;
const RING_C = 2 * Math.PI * RING_R;

function ConfidenceRing({ confidence }: { confidence: number | null | undefined }) {
  return (
    <svg className="ing-conf-ring" viewBox="0 0 20 20" aria-hidden="true">
      <circle className="ing-conf-ring-bg" cx="10" cy="10" r={RING_R} />
      <circle
        className="ing-conf-ring-fg"
        cx="10" cy="10" r={RING_R}
        strokeDasharray={RING_C}
        strokeDashoffset={RING_C * (1 - (confidence ?? 0))}
      />
    </svg>
  );
}

interface SampleReviewEntry {
  job: any;
  plan: any;
  breakdown?: any;
}

interface SampleReviewProps {
  sample: SampleReviewEntry;
  onBack: () => void;
  onToast?: (msg: string) => void;
}

function ScheduleReview({ sample, onBack }: { sample: SampleReviewEntry; onBack: () => void }) {
  const { job, plan } = sample;
  const blast = blastText(plan?.blast_radius);
  return (
    <>
      <button className="ingest-back" onClick={onBack}>← Ingest queue</button>
      <div className="ingest-review ing-sched-review" style={{ gridTemplateColumns: '1fr' }}>
        <div className="ingest-rev-head ing-navy-head">
          <span className="ingest-rev-name ing-navy-head-t" title={job.original_filename}>{job.original_filename}</span>
          {plan?.risk_class ? <span className={`ingest-chip ingest-chip-${plan.risk_class}`}>{plan.risk_class}</span> : null}
          <span className="ingest-chip ingest-chip-info">IMS</span>
        </div>
        {blast ? (
          <div className="ingest-blast">
            <span className="ingest-blast-lab">Blast radius</span>
            <span>{blast}</span>
          </div>
        ) : null}
        <div className="ing-sched-tiles">
          <div className="ing-tile">
            <span className="ing-tile-l">Risk</span>
            <span className="ing-tile-v" style={{ fontSize: 20 }}>{plan?.risk_class ? humanize(plan.risk_class) : '—'}</span>
          </div>
        </div>
        <div className="ingest-rev-foot">
          <span className="ingest-foot-note">{humanize(job.status || 'settled')}</span>
          <span className="ingest-foot-push" />
          <button className="btn btn--primary" onClick={onBack}>Back to queue</button>
        </div>
      </div>
    </>
  );
}

export default function SampleReview({ sample, onBack, onToast }: SampleReviewProps) {
  const { job, plan, breakdown } = sample;
  const pageRef = useRef<HTMLDivElement>(null);

  const fields = useMemo(() => breakdown?.fields || [], [breakdown]);
  const flags = useMemo(
    () => fields.filter((f: any) => f.flag).map((f: any) => ({ ...f.flag, fieldKey: f.key })),
    [fields]
  );

  const initialFlagId = useMemo(() => {
    const blocker = flags.find((f: any) => f.kind === 'blocker');
    return (blocker || flags[0])?.id ?? null;
  }, [flags]);

  const [selectedFlagId, setSelectedFlagId] = useState<string | null>(initialFlagId);
  const [decisions, setDecisions] = useState<Record<string, string | undefined>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, any>>({});
  const [confirming, setConfirming] = useState(false);

  const toggleFlag = useCallback((id: string) => {
    setSelectedFlagId((prev) => (prev === id ? null : id));
  }, []);

  const setDecision = useCallback((key: string, status: string) => {
    setDecisions((prev) => ({ ...prev, [key]: prev[key] === status ? undefined : status }));
    setEditing((cur) => (cur === key ? null : cur));
    setConfirming(false);
  }, []);

  const startEdit = useCallback((field: any) => {
    setEditing(field.key);
    setEdits((prev) => ({ ...prev, [field.key]: prev[field.key] ?? field.value }));
    setDecisions((prev) => (prev[field.key] === 'rejected' ? { ...prev, [field.key]: undefined } : prev));
    setConfirming(false);
  }, []);

  const blockers = Array.isArray(plan?.blocking_errors) ? plan.blocking_errors : [];
  const blocked = blockers.length > 0;
  const isCritical = plan?.risk_class === 'critical';

  const rejectedCount = fields.filter((f: any) => decisions[f.key] === 'rejected').length;
  const acceptedCount = fields.length - rejectedCount;

  const approve = useCallback(() => {
    if (blocked) return;
    if (acceptedCount === 0) { onToast?.('Nothing to approve — every field is rejected.'); return; }
    if (isCritical && !confirming) {
      setConfirming(true);
      onToast?.('Critical change — press Approve again to confirm.');
      return;
    }
    onToast?.('Sample document — nothing was written.');
    onBack?.();
  }, [blocked, acceptedCount, isCritical, confirming, onToast, onBack]);

  const rejectDocument = useCallback(() => {
    onToast?.(`${job?.original_filename || 'Document'} rejected — this is a sample, nothing was written.`);
    onBack?.();
  }, [job, onToast, onBack]);

  if (job.doc_type === 'schedule' || !breakdown) {
    return <ScheduleReview sample={sample} onBack={onBack} />;
  }

  const sampleKey = String(job.id).replace(/^sample-/, '');
  const anchorId = (fieldKey: string) => `ing-anchor-${sampleKey}-${fieldKey}`;

  const entityConf = pct(job.entity_confidence);
  const entityPctNum = job.entity_confidence == null ? 0 : Math.round(Number(job.entity_confidence) * 100);
  const lowMatch = job.entity_confidence != null && Number(job.entity_confidence) < 0.92;

  const metaLine = [
    breakdown.entity,
    job.sha256 ? `sha256 ${job.sha256}` : null,
    job.model_id || null,
    job.prompt_version ? `prompt ${job.prompt_version}` : null,
  ].filter(Boolean).join(' · ');

  const annotations = flags.map((f: any) => ({
    id: f.id,
    anchorId: anchorId(f.fieldKey),
    severity: f.kind === 'blocker' ? 'critical' : 'warn',
    title: f.title,
    body: f.body,
    selected: selectedFlagId === f.id,
  }));

  const renderDocLine = (line: any, i: number) => {
    if (line.t === 'title') return <div key={i} className="ing-brk-doc-title">{line.text}</div>;
    if (line.t === 'bar') return <div key={i} className="ing-brk-doc-bar" style={{ width: line.w }} aria-hidden="true" />;
    if (line.t === 'total') {
      return (
        <div key={i} className="ing-doc-line ing-brk-doc-total">
          <span id={anchorId(line.f)} className="ing-brk-doc-block">{line.text}</span>
        </div>
      );
    }
    return (
      <div key={i} className="ing-doc-line">
        {line.seg.map((s: any, j: number) => (
          s.f
            ? <span key={j} id={anchorId(s.f)} className="ing-brk-doc-mark">{s.text}</span>
            : <span key={j}>{s.text}</span>
        ))}
      </div>
    );
  };

  return (
    <>
      <button className="ingest-back" onClick={onBack}>← Ingest queue</button>
      <div className="ingest-review ing-brk">
        <div className="ingest-rev-head ing-navy-head ing-brk-head">
          <span className="ingest-rev-name ing-navy-head-t" title={job.original_filename}>{job.original_filename}</span>
          {plan?.risk_class ? <span className={`ingest-chip ingest-chip-${plan.risk_class}`}>{plan.risk_class}</span> : null}
          {job.doc_type ? <span className="ingest-chip ingest-chip-info">{humanize(job.doc_type)}</span> : null}
          {entityConf ? (
            <span className={`ing-brk-match${lowMatch ? ' warn' : ''}`}>
              <span className="ing-brk-match-l">Entity Match</span>
              <b>{entityConf}</b>
              <span className="ing-brk-meter" aria-hidden="true">
                <i style={{ width: `${entityPctNum}%` }} />
              </span>
            </span>
          ) : null}
          <div className="ing-brk-meta">{metaLine}</div>
        </div>

        {flags.length ? (
          <div className="ing-brk-flags">
            <span className="ing-brk-flags-lab">Flagged</span>
            {flags.map((f: any) => (
              <button
                key={f.id}
                type="button"
                className={`ing-brk-flag ing-brk-flag-${f.kind}${selectedFlagId === f.id ? ' on' : ''}`}
                aria-pressed={selectedFlagId === f.id}
                onClick={() => toggleFlag(f.id)}
              >
                <span className="ing-brk-flag-dot" aria-hidden="true" />
                {f.title}
              </button>
            ))}
            <span className="ing-brk-flags-hint">Select a flag to locate it on the page</span>
          </div>
        ) : null}

        {blocked ? (
          <div className="ingest-blocker">
            <b>⛔ Cannot apply</b>
            <span>{blockers.map((b: any) => (typeof b === 'string' ? b : b.detail || b.message || JSON.stringify(b))).join(' ')}</span>
          </div>
        ) : null}

        {breakdown.blast ? (
          <div className="ingest-blast ing-brk-blast">
            <span className="ingest-blast-lab">Blast radius</span>
            <span>{breakdown.blast}</span>
          </div>
        ) : null}

        <div className="ing-brk-body">
          <div className="ing-brk-left">
            <div className="ing-doc-pane">
              <div className="ing-doc-page" ref={pageRef}>
                {(breakdown.doc || []).map(renderDocLine)}
              </div>
              <AnnotationLayer
                pageRef={pageRef}
                annotations={annotations}
                onSelect={toggleFlag}
              />
            </div>
            {breakdown.pageNote ? (
              <div className="ing-brk-pagenote">{breakdown.pageNote}</div>
            ) : null}
          </div>

          <div className="ing-brk-rail">
            {fields.map((f: any) => {
              const flag = f.flag;
              const decision = decisions[f.key];
              const isEditing = editing === f.key;
              const value = edits[f.key] != null ? edits[f.key] : f.value;
              const low = f.confidence != null && Number(f.confidence) < LOW_CONFIDENCE;
              const selected = flag && selectedFlagId === flag.id;
              return (
                <div
                  key={f.key}
                  data-field={f.key}
                  className={[
                    'ing-brk-fld',
                    flag ? `ing-brk-fld-${flag.kind}` : '',
                    selected ? 'on' : '',
                    decision === 'rejected' ? 'rejected' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => { if (flag) setSelectedFlagId(flag.id); }}
                >
                  <div className="ing-brk-fld-top">
                    <span className="ing-brk-fld-k">{f.label}</span>
                    {flag ? (
                      <button
                        type="button"
                        className={`ing-brk-badge ing-brk-badge-${flag.kind}`}
                        aria-label={`Locate ${flag.title} on the page`}
                        aria-pressed={selected}
                        onClick={(e) => { e.stopPropagation(); toggleFlag(flag.id); }}
                      >{flag.kind}</button>
                    ) : null}
                    <span className={`ing-brk-fld-conf${low ? ' warn' : ''}`}>
                      <ConfidenceRing confidence={f.confidence} />
                      {pct(f.confidence)}
                    </span>
                  </div>

                  <div className="ing-brk-fld-mid">
                    <div className="ing-brk-fld-v">
                      {isEditing ? (
                        <input
                          className="ingest-field-edit"
                          value={value == null ? '' : String(value)}
                          aria-label={f.label}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setEdits((prev) => ({ ...prev, [f.key]: e.target.value }))}
                        />
                      ) : displayValue(value)}
                    </div>

                    <div className="ing-brk-fld-acts">
                      <button
                        type="button"
                        className={`ingest-act${decision === 'accepted' && !isEditing ? ' on-ok' : ''}`}
                        title="Accept" aria-label={`Accept ${f.label}`}
                        onClick={(e) => { e.stopPropagation(); setDecision(f.key, 'accepted'); }}
                      >✓</button>
                      <button
                        type="button"
                        className={`ingest-act${isEditing ? ' on-ed' : ''}`}
                        title="Edit" aria-label={`Edit ${f.label}`}
                        onClick={(e) => { e.stopPropagation(); if (isEditing) setEditing(null); else startEdit(f); }}
                      >✎</button>
                      <button
                        type="button"
                        className={`ingest-act${decision === 'rejected' ? ' on-no' : ''}`}
                        title="Reject" aria-label={`Reject ${f.label}`}
                        onClick={(e) => { e.stopPropagation(); setDecision(f.key, 'rejected'); }}
                      >✕</button>
                    </div>
                  </div>

                  {flag ? (
                    <div className={`ing-brk-note ing-brk-note-${flag.kind}`}>
                      <span className="ing-brk-note-lab">{flag.kind}</span>
                      <span className="ing-brk-note-t"><b>{flag.title}.</b> {flag.body}</span>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        <div className="ingest-rev-foot">
          <span className="ingest-foot-note">
            {acceptedCount} accepted · {rejectedCount} rejected · {flags.length} flagged
            {isCritical ? ' · second confirmation required' : ''}
          </span>
          <span className="ingest-foot-push" />
          <button className="btn" onClick={rejectDocument}>
            Reject document
          </button>
          <button
            className="btn btn--primary ing-gold-btn"
            onClick={approve}
            disabled={blocked}
          >
            {blocked
              ? 'Blocked — resolve first'
              : confirming
                ? 'Confirm approval'
                : `Approve ${acceptedCount} change${acceptedCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </>
  );
}
